import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/eastenderz-hi-ibiza-1-september-2026";
const IMAGE_URL = "/assets/community/zzeastenddubsdonee.png";
const OFFICIAL_TICKET_URL = "https://www.hiibiza.com/events/2026/eastenderz/2026-09-01";
const SPOTLIGHT_TICKET_URL = "https://www.ibiza-spotlight.com/shop/tickets/choose.php?event_id=425564";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const THEATRE_LINEUP = ["East End Dubs", "Miguelle & Tons", "Job De Jong", "Delilah"];
const CLUB_ROOM_LINEUP = ["Paco Osuna", "Joëlla Jackson", "Easttown"];
const ALL_ARTISTS = [...THEATRE_LINEUP, ...CLUB_ROOM_LINEUP];

const KEYWORDS = [
  "East End Dubs Hï Ibiza 1 September 2026 tickets",
  "Eastenderz Hï Ibiza tickets",
  "East End Dubs presents Eastenderz Hï Ibiza",
  "Miguelle and Tons Hï Ibiza",
  "Job De Jong Hï Ibiza",
  "Delilah Hï Ibiza Eastenderz",
  "Paco Osuna Now Here Hï Ibiza",
  "Joëlla Jackson Hï Ibiza",
  "Easttown Hï Ibiza",
  "Hï Ibiza Tuesday tickets",
  "Eastenderz Ibiza 2026",
  "HLT Ibiza concierge",
];

const FAQS = [
  {
    question: "When is East End Dubs presents Eastenderz at Hï Ibiza?",
    answer: "East End Dubs presents Eastenderz is at Hï Ibiza on Tuesday 1 September 2026, with doors listed from 23:30.",
  },
  {
    question: "Who is playing Eastenderz at Hï Ibiza on 1 September 2026?",
    answer: "Theatre lists East End Dubs, Miguelle & Tons, Job De Jong and Delilah. Club Room / Now Here lists Paco Osuna, Joëlla Jackson and Easttown.",
  },
  {
    question: "Where can I buy Eastenderz Hï Ibiza tickets?",
    answer: "Use the official Hï Ibiza event page or the Ibiza Spotlight ticket checkout linked in this HLT guide. Entry before midnight was listed from 30 EUR when checked, but live availability can change.",
  },
  {
    question: "Where is Hï Ibiza?",
    answer: "Hï Ibiza is in Playa d'en Bossa, close to Ushuaïa Ibiza and the main resort strip, making it one of the easiest Ibiza superclubs to build a full night around.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "East End Dubs presents Eastenderz at Hï Ibiza 1 September 2026: Tickets, Lineup and HLT Guide",
  description:
    "East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 1 September 2026 with Miguelle & Tons, Job De Jong, Delilah, Paco Osuna, Joëlla Jackson and Easttown.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-09-01",
  dateModified: "2026-09-01",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "East End Dubs presents Eastenderz at Hï Ibiza",
  startDate: "2026-09-01T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "Hï Ibiza",
    address: "Playa d'en Bossa, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: OFFICIAL_TICKET_URL,
    priceCurrency: "EUR",
    price: "30",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy Eastenderz Tickets" }) {
  return (
    <a href={OFFICIAL_TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function SpotlightTicketCta({ children = "Ibiza Spotlight Tickets" }) {
  return (
    <a href={SPOTLIGHT_TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function EastenderzHiIbizaSeptemberArticle() {
  return (
    <>
      <Helmet>
        <title>East End Dubs Hï Ibiza 1 September 2026 Tickets | HLT Guide</title>
        <meta
          name="description"
          content="East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 1 September 2026. Lineup, official tickets, Ibiza Spotlight ticket link and HLT Ibiza planning guide."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="East End Dubs Hï Ibiza 1 September 2026 Tickets" />
        <meta property="og:description" content="Official tickets, lineup and HLT planning guide for Eastenderz at Hï Ibiza." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="East End Dubs Hï Ibiza 1 September 2026 Tickets" />
        <meta name="twitter:description" content="Eastenderz takes over Hï Ibiza with East End Dubs, Miguelle & Tons, Job De Jong, Delilah and Now Here." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Tonight</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              East End Dubs presents Eastenderz at Hï Ibiza: 1 September 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Eastenderz lands back in The Theatre at Hï Ibiza tonight, with East End Dubs joined by Miguelle & Tons, Job De Jong and Delilah. Club Room / Now Here keeps the pressure on with Paco Osuna, Joëlla Jackson and Easttown.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official Hï tickets" />
              <SpotlightTicketCta />
              <ConciergeCta children="Plan Hï Ibiza with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>East End Dubs presents Eastenderz</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Hï Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Tuesday 1 September 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>From 30 EUR when checked</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Deep tech, minimal house, rolling groove and Now Here club-room pressure</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src={IMAGE_URL} alt="East End Dubs presents Eastenderz at Hï Ibiza 1 September 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why tonight matters">
              <p>
                Eastenderz has become one of the sharpest Tuesday choices in Ibiza because it brings a club-focused sound into a superclub setting. East End Dubs keeps the identity tight: deep tech, minimal house, garage-tinted swing and rolling basslines built for dancers rather than quick hits.
              </p>
              <p>
                Tonight matters because the lineup gives Hï Ibiza a proper underground lane without losing scale. The Theatre gets Eastenderz in full flow, while Club Room / Now Here adds Paco Osuna's harder, more percussive energy for anyone who wants a second room with bite.
              </p>
            </ArticleSection>

            <ArticleSection title="The Theatre: East End Dubs, Miguelle & Tons, Job De Jong and Delilah">
              <p>
                East End Dubs is the centrepiece, and this is exactly the kind of room his sound was made for: precise drums, stripped grooves, patient blends and a low-end that can sit heavy without overcrowding the night.
              </p>
              <p>
                Miguelle & Tons add a slick, modern house edge, Job De Jong brings a clean Amsterdam groove sensibility, and Delilah gives the lineup a fresh UK selector feel. Together, it should make The Theatre feel detailed, rolling and properly locked in from late to close.
              </p>
            </ArticleSection>

            <ArticleSection title="Club Room / Now Here">
              <p>
                The Club Room is Paco Osuna's Now Here territory, joined tonight by Joëlla Jackson and Easttown. This is where the night can turn darker, tighter and more driving, with a different kind of momentum from The Theatre.
              </p>
              <p>
                The smart move is to use both rooms. Start with Eastenderz for the groove, then step into Now Here when you want heavier percussion and a room that feels closer, tougher and more direct.
              </p>
            </ArticleSection>

            <ArticleSection title="Tickets and timing">
              <p>
                We are linking directly to the official Hï Ibiza event page for East End Dubs presents Eastenderz on Tuesday 1 September 2026. Hï's listing shows entry before midnight from 30 EUR, entry before 1am from 40 EUR and standard tickets from 50 EUR when checked.
              </p>
              <p>
                Ibiza Spotlight also has a direct checkout page for the same date. Prices and availability can change quickly on event day, so use the live ticket links before making the rest of your plan.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on Hï Ibiza" />
                <SpotlightTicketCta children="Buy via Ibiza Spotlight" />
                <ConciergeCta children="Ask HLT to arrange the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="Where to stay for Hï Ibiza">
              <p>
                Playa d'en Bossa is the easiest base if Eastenderz is the centre of the trip. You are close to Hï Ibiza, Ushuaïa, beach clubs and late-night food, which keeps the night clean and avoids a long post-club transfer.
              </p>
              <p>
                Ibiza Town is better if you want dinner, marina energy and a more polished start before the club. San Antonio can work for sunset plans and budget-led groups, but you need taxis or transfers sorted before the night gets busy.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><strong className="text-white">Event:</strong> East End Dubs presents Eastenderz</li>
                <li><strong className="text-white">Venue:</strong> Hï Ibiza, Playa d'en Bossa</li>
                <li><strong className="text-white">Date:</strong> Tuesday 1 September 2026</li>
                <li><strong className="text-white">Time:</strong> 23:30</li>
                <li><strong className="text-white">The Theatre:</strong> {THEATRE_LINEUP.join(", ")}</li>
                <li><strong className="text-white">Club Room / Now Here:</strong> {CLUB_ROOM_LINEUP.join(", ")}</li>
                <li><strong className="text-white">Tickets:</strong> From 30 EUR when checked</li>
                <li><strong className="text-white">Official ticket link:</strong> <a href={OFFICIAL_TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">Hï Ibiza event tickets</a></li>
                <li><strong className="text-white">Direct checkout:</strong> <a href={SPOTLIGHT_TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">Ibiza Spotlight tickets</a></li>
              </ul>
            </ArticleSection>

            <ArticleSection title="HLT verdict">
              <p>
                Eastenderz at Hï Ibiza tonight is the right pick if you want the island's biggest production values without losing the underground house thread. It is music-first, but still built for a proper Ibiza night out.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy Eastenderz tickets" />
                <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events -&gt;</Link>
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/20 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT trip builder</p>
              <h3 className="mt-3 font-display text-3xl leading-none text-white">Lock the ticket, then build the night.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Need a hotel, villa, transfer, table or group plan around Eastenderz at Hï Ibiza? HLT can shape the night around the event.
              </p>
              <div className="mt-4 grid gap-3">
                <TicketCta children="Official Hï tickets" />
                <SpotlightTicketCta children="Ibiza Spotlight checkout" />
                <ConciergeCta children="Concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 grid gap-3 text-sm leading-6 text-slate-300">
                <li><strong className="text-white">Arrive with a plan:</strong> Hï has multiple rooms and a late start.</li>
                <li><strong className="text-white">Book official:</strong> use live ticket links for current availability.</li>
                <li><strong className="text-white">Use both rooms:</strong> The Theatre gives Eastenderz groove, Club Room brings Now Here pressure.</li>
              </ul>
            </div>
          </aside>
        </div>

        <section className="space-y-4 border-t border-white/10 pt-8">
          <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
          <div className="grid gap-3">
            {FAQS.map((item) => (
              <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}
